import { skipToken } from '@reduxjs/toolkit/query';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { FormContainer } from '@strata/react-form-container';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import {
    EMAIL_ALREADY_IN_USE_MESSAGE,
    formatUserNameFirstLast,
    generateRandomPassword,
    getMarketplaceRoleSelectValues,
    getRoleNameForDisplayName,
    parseFullName,
} from 'components/admin/userAccounts/userAccountHelpers';
import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import {
    useCreateOrganizationUserMutation,
    useGetOrganizationUserQuery,
    useGetOrganizationUserRolesQuery,
    useUpdateOrganizationUserMutation,
} from 'store/api/authApi';

const FORM_FIELDS_MAX_WIDTH = 360;

const validationSchema = Yup.object({
    firstName: Yup.string().trim().required(validationStrings.requiredField.errorMessage),
    lastName: Yup.string().trim().required(validationStrings.requiredField.errorMessage),
    upn: Yup.string()
        .trim()
        .email('Enter a valid email address')
        .required(validationStrings.requiredField.errorMessage),
    roleDisplayName: Yup.string().required(validationStrings.requiredField.errorMessage),
});

export const UserAccountFormPage = () => {
    const { upn } = useParams() as { upn?: string };
    const navigate = useNavigate();
    const userCanEdit = usePermissionCheck(Permissions.WRITE_SUPPLIER_USERS);
    const isCreate = !upn;

    const { data: roles, isFetching: rolesLoading } = useGetOrganizationUserRolesQuery();
    const {
        data: organizationUser,
        isFetching: userLoading,
        error: userError,
    } = useGetOrganizationUserQuery(upn ?? skipToken);

    const [createOrganizationUser] = useCreateOrganizationUserMutation();
    const [updateOrganizationUser] = useUpdateOrganizationUserMutation();

    const roleSelectValues = getMarketplaceRoleSelectValues(roles);

    useEffect(() => {
        if (!userCanEdit) {
            navigate(AppRoutes.userAccounts);
        }
    }, [navigate, userCanEdit]);

    useEffect(() => {
        if (!isCreate && !userLoading && !organizationUser) {
            if (!userError) {
                handleError('User account not found.');
            }
            navigate(AppRoutes.userAccounts);
        }
    }, [isCreate, navigate, organizationUser, userError, userLoading]);

    if (!userCanEdit) {
        return null;
    }

    const parsedName = organizationUser ? parseFullName(organizationUser.full_name) : undefined;

    return (
        <FormPageContainer>
            <FullscreenLoading
                isShowing={rolesLoading || (!isCreate && userLoading)}
                loadingText="Loading user account."
            />
            <div
                className="flex__gap--medium padding__around--medium"
                style={{ flexDirection: 'column', height: '100%' }}
            >
                <ActionButtonPageTitle
                    label="User Accounts"
                    title={isCreate ? 'Add User Account' : 'Update User Account'}
                    id="user-account-form-page-title-button"
                    onClick={() => navigate(AppRoutes.userAccounts)}
                />
                <InlineMessage
                    intent={InlineMessage.Intent.INFO}
                    wrapText
                    label="New users will be sent an email with instructions for setting up their FMH for Pharmacies account."
                />
                <Formik
                    initialValues={{
                        firstName: parsedName?.firstName ?? '',
                        lastName: parsedName?.lastName ?? '',
                        upn: organizationUser?.upn ?? '',
                        roleDisplayName: organizationUser?.display_name ?? '',
                    }}
                    validationSchema={validationSchema}
                    enableReinitialize
                    onSubmit={async (values, { setFieldError }) => {
                        const fullName = formatUserNameFirstLast(values.firstName, values.lastName);
                        const roleName = getRoleNameForDisplayName(values.roleDisplayName, roles);
                        if (!roleName) {
                            setFieldError(
                                'roleDisplayName',
                                validationStrings.requiredField.errorMessage,
                            );
                            return;
                        }
                        try {
                            if (isCreate) {
                                await createOrganizationUser({
                                    upn: values.upn.trim(),
                                    given_name: values.firstName.trim(),
                                    family_name: values.lastName.trim(),
                                    name: fullName,
                                    password: generateRandomPassword(),
                                    role_name: roleName,
                                    sms_number: null,
                                    analytics_access: false,
                                }).unwrap();
                                SuccessToast({
                                    message: `${fullName} user account successfully added. An account setup email has been sent to them.`,
                                });
                            } else {
                                if (!upn || !organizationUser) {
                                    return;
                                }
                                await updateOrganizationUser({
                                    upn,
                                    body: {
                                        full_name: fullName,
                                        sms_number: organizationUser.sms_number,
                                        role_name: roleName,
                                        analytics_access: organizationUser.analytics_access,
                                    },
                                }).unwrap();
                                SuccessToast({
                                    message: `${fullName}'s user account successfully updated.`,
                                });
                            }
                            navigate(AppRoutes.userAccounts);
                        } catch (err) {
                            const error = err as { status?: number };
                            if (isCreate && error.status === 409) {
                                setFieldError('upn', EMAIL_ALREADY_IN_USE_MESSAGE);
                                return;
                            }
                            handleError(
                                `Error ${isCreate ? 'creating' : 'updating'} user account.`,
                                err as Error,
                            );
                        }
                    }}
                >
                    {(formik) => (
                        <div style={{ overflow: 'auto', flexGrow: 1 }}>
                            <FormContainer
                                formTitle={
                                    isCreate ? 'Add New User Account' : 'Update User Account'
                                }
                                onFormSubmit={formik.handleSubmit}
                                onCancelClick={() => navigate(AppRoutes.userAccounts)}
                                isSubmitting={
                                    !(formik.dirty && formik.isValid) || formik.isSubmitting
                                }
                                formLeftContent={
                                    <div style={{ maxWidth: FORM_FIELDS_MAX_WIDTH }}>
                                        <TextInputFormik
                                            id="firstName"
                                            label="First Name"
                                            required
                                        />
                                        <TextInputFormik id="lastName" label="Last Name" required />
                                        <TextInputFormik
                                            id="upn"
                                            label="Email"
                                            required
                                            disabled={!isCreate}
                                        />
                                        <SuggestSingleSelectFormik
                                            id="roleDisplayName"
                                            label="Role"
                                            placeholderText="Select role"
                                            required
                                            values={roleSelectValues}
                                            displaySecondaryDataBelowOption
                                            onChange={(selected) => {
                                                void formik.setFieldValue(
                                                    'roleDisplayName',
                                                    selected?.name ?? '',
                                                );
                                            }}
                                        />
                                    </div>
                                }
                            />
                        </div>
                    )}
                </Formik>
            </div>
        </FormPageContainer>
    );
};
